import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { AlertCircle, Mail } from 'lucide-react';

const ERROR_MESSAGES = {
  invalid_token: {
    title: 'Invalid Claim Link',
    message: 'This claim link is invalid or has expired. Please request a new one from your agent registration.',
    showRetry: false,
  },
  already_claimed: {
    title: 'Already Claimed',
    message: 'This agent has already been claimed. If you own this agent, please sign in instead.',
    showRetry: false,
  },
  user_has_agent: {
    title: 'One Agent Per Account',
    message: 'Your X account already owns an agent. Each account can only claim one agent.',
    showRetry: false,
  },
  expired_state: {
    title: 'Session Expired',
    message: 'Your session has expired for security reasons. Please start the claim process again.',
    showRetry: true,
  },
  server_error: {
    title: 'Something Went Wrong',
    message: 'We encountered an error while processing your request. Please try again in a few moments.',
    showRetry: true,
  },
  default: {
    title: 'Error',
    message: 'An unexpected error occurred. Please try again or contact support.',
    showRetry: true,
  },
} as const;

export function ClaimErrorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const errorCode = searchParams.get('error') as keyof typeof ERROR_MESSAGES | null;
  const errorInfo = errorCode && errorCode in ERROR_MESSAGES 
    ? ERROR_MESSAGES[errorCode] 
    : ERROR_MESSAGES.default;

  const handleRetry = () => {
    navigate(-1); // Go back to previous page (claim page)
  };

  const handleContactSupport = () => {
    // Open email client with pre-filled support email
    window.location.href = 'mailto:support@clawstarter.io?subject=Agent%20Claim%20Issue';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <Logo size={64} />
          </div>
          <div className="space-y-3">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-destructive">
              {errorInfo.title}
            </CardTitle>
            <CardDescription className="text-base">
              {errorInfo.message}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Error Details (if we have an error code) */}
          {errorCode && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
              <p className="text-xs text-muted-foreground text-center">
                Error code: <span className="font-mono">{errorCode}</span>
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {errorInfo.showRetry && (
              <Button
                onClick={handleRetry}
                className="w-full h-11"
                size="lg"
              >
                Try Again
              </Button>
            )}

            {errorCode === 'already_claimed' && (
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-11"
                size="lg"
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Sign In with X
                </span>
              </Button>
            )}

            {errorCode === 'user_has_agent' && (
              <Button
                onClick={() => navigate('/')}
                className="w-full h-11"
                size="lg"
              >
                Go to Dashboard
              </Button>
            )}

            {errorCode === 'invalid_token' && (
              <Button
                onClick={() => navigate('/')}
                className="w-full h-11"
                size="lg"
              >
                Go to Dashboard
              </Button>
            )}

            <Button
              onClick={handleContactSupport}
              variant="outline"
              className="w-full h-11"
              size="lg"
            >
              <span className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Contact Support
              </span>
            </Button>
          </div>

          {/* Help Text */}
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Need help?</span>
              <br />
              {errorCode === 'invalid_token' && 
                'Check your agent registration to get a new claim link.'}
              {errorCode === 'already_claimed' && 
                'If you believe this is an error, please contact our support team.'}
              {errorCode === 'user_has_agent' && 
                'To claim a different agent, you would need to use a different X account.'}
              {errorCode === 'expired_state' && 
                'For security, claim sessions expire after a short time. Simply start the process again.'}
              {errorCode === 'server_error' && 
                'Our team has been notified. If the problem persists, please reach out to support.'}
              {!errorCode && 
                'Contact our support team for assistance with your claim.'}
            </p>
          </div>

          {/* Footer */}
          <div className="text-center pt-2">
            <button
              onClick={() => navigate('/')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Return to home
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
